import { createContext, useContext, useState, useRef, useEffect, lazy, Suspense } from "react";
import { Outlet, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { BottomNav } from "@/components/BottomNav";
import { SeoFooter } from "@/components/SeoFooter";
import { InstallPrompt } from "@/components/InstallPrompt";
import { NotificationPrompt } from "@/components/NotificationPrompt";
import { ProfileLocationPrompt } from "@/components/ProfileLocationPrompt";
import { PaidInfoPrompt } from "@/components/PaidInfoPrompt";
import { BackButton } from "@/components/BackButton";
import { api } from "@/lib/appresse";
import { useAuth } from "@/lib/auth";
import { toast } from "@/components/ui/sonner";

// Lazy: react-pageflip is heavy and only needed once a revue is opened.
const PdfViewer = lazy(() => import("@/components/PdfViewer").then((m) => ({ default: m.PdfViewer })));
// Lazy: chat is only shown on /ambassadeur.
const ChatWidget = lazy(() => import("@/components/ChatWidget").then((m) => ({ default: m.ChatWidget })));

const SEO = {
  "/": ["APP PRESSE — Kiosque numérique : journaux et magazines en illimité dès 1 €",
    "APP PRESSE, le kiosque numérique de la presse française : quotidiens, presse régionale, internationale et magazines en illimité. Dès 1 € le 1er mois, sans engagement."],
  "/explorer": ["Explorer tous les journaux et magazines — APP PRESSE",
    "Parcourez tous les titres de presse sur APP PRESSE : actualité, sport, économie, people, auto, tech, cuisine, voyage, presse régionale et internationale."],
  "/compte/abonnement": ["Formules SOLO, DUO, FAMILLE dès 1 € — APP PRESSE",
    "Choisissez votre Espace APP PRESSE : SOLO 1 € le 1er mois puis 7,90 €/mois, DUO 9,90 €, FAMILLE 12,90 € TTC. Sans engagement, résiliable en un clic."],
  "/compte": ["Mon espace — APP PRESSE", "Connectez-vous à votre espace APP PRESSE."],
};

const ReaderCtx = createContext(null);
export const useReader = () => useContext(ReaderCtx);

export default function Layout() {
  const [reading, setReading] = useState(null);
  const { user, refreshUser } = useAuth();
  useEffect(() => { api.trackVisit(); }, [user?.id]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const evRef = useRef({ id: null, activeMs: 0, segStart: 0 });
  // Capture ?edition= at first render, before child routes can strip the query param.
  const pendingEditionRef = useRef(searchParams.get("edition"));

  const requireAccount = () => navigate("/compte");

  // On ne compte que le temps réellement actif au premier plan : le compteur se
  // met en pause dès que l'app passe en arrière-plan / l'écran se verrouille /
  // l'onglet est masqué, et reprend au retour. Évite de compter le temps "veille".
  const bankActive = () => {
    const ev = evRef.current;
    if (ev.segStart) {
      ev.activeMs += Date.now() - ev.segStart;
      ev.segStart = 0;
    }
  };
  const flushDuration = () => {
    const ev = evRef.current;
    const dur = Math.round(ev.activeMs / 1000);
    if (ev.id && dur > 0) api.setDuration(ev.id, dur).catch(() => {});
  };

  const subRef = useRef(null);
  const startReading = (edition) => {
    setReading(edition);
    evRef.current = { id: null, activeMs: 0, segStart: Date.now() };
    api.track({ type: "consultation", edition_id: edition.id })
      .then((d) => { evRef.current.id = d.event_id; })
      .catch(() => {});
  };

  const openReader = async (edition) => {
    if (!user) {
      requireAccount();
      return;
    }
    if (user.email_verified === false) {
      toast.message("Confirmez votre e-mail pour lire les revues", {
        description: "Saisissez le code reçu par e-mail dans votre compte.",
      });
      navigate("/compte");
      return;
    }
    // Péage : le lecteur s'ouvre tout de suite, l'abonnement est vérifié en parallèle.
    const cached = subRef.current && subRef.current.at > Date.now() - 300000 ? subRef.current.st : null;
    if (!cached || (!cached.enabled || (cached.has_access && !cached.paid_info_required))) startReading(edition);
    try {
      const st = await api.mySubscription();
      subRef.current = { st, at: Date.now() };
      if (st?.enabled && (!st.has_access || st.paid_info_required)) setReading(null);
      if (st?.enabled && !st.has_access) {
        toast.message("Choisissez une formule pour lire les revues", {
          description: "Accès illimité à APPPRESSE.COM selon votre besoin.",
        });
        navigate("/compte/abonnement");
        return;
      }
      if (st?.enabled && st.paid_info_required) {
        await refreshUser();
        return;
      }
    } catch { /* en cas d'erreur réseau, on laisse ouvrir */ }
  };

  // Deep-link: ?edition=<id> (captured at first render). Opens the revue once
  // auth has hydrated. If the visitor isn't signed in yet, we keep the pending
  // edition and prompt them — then open that exact revue right after they log in
  // or create their account (so a shared link always lands on the revue).
  useEffect(() => {
    const eid = pendingEditionRef.current;
    if (!eid || user === null) return; // still hydrating
    if (user === false) {
      requireAccount();
      return; // keep pendingEditionRef so we open it after login/signup
    }
    if (user.email_verified === false) {
      navigate("/compte"); // must confirm e-mail first
      return;
    }
    pendingEditionRef.current = null;
    api.edition(eid).then((e) => openReader(e)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const closeReader = () => {
    bankActive();
    flushDuration();
    evRef.current = { id: null, activeMs: 0, segStart: 0 };
    setReading(null);
  };

  // Pause/reprise du compteur de lecture selon l'état d'affichage (arrière-plan,
  // écran verrouillé, onglet masqué). On persiste aussi la durée à chaque mise en
  // arrière-plan, au cas où l'app serait tuée sans fermeture propre du lecteur.
  useEffect(() => {
    if (!reading) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        bankActive();
        flushDuration();
      } else if (!evRef.current.segStart) {
        evRef.current.segStart = Date.now();
      }
    };
    const onPageHide = () => { bankActive(); flushDuration(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reading]);

  // Prospect (e-mail non confirmé) : bloqué sur l'écran de confirmation tant
  // qu'il n'a pas activé son compte — aucune navigation dans le kiosque.
  useEffect(() => {
    if (user && user.email_verified === false && location.pathname !== "/compte") {
      navigate("/compte", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    const m = SEO[location.pathname] || SEO["/"];
    document.title = m[0];
    document.querySelector('meta[name="description"]')?.setAttribute("content", m[1]);
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", `https://apppresse.com${location.pathname}`);
  }, [location.pathname]);

  // Client activé sans Espace : il peut parcourir librement l'accueil et son
  // compte. Le péage ne se déclenche QUE sur « Lire » (openReader), qui le
  // renvoie alors vers « Choisir mon Espace ».

  return (
    <ReaderCtx.Provider value={{ openReader, requireAccount }}>
      <div className="app-shell flex flex-col bg-background">
        <TopBar />
        <main className="app-scroll flex-1 overflow-y-auto overflow-x-hidden pb-6">
          {user?.email_verified !== false ? <BackButton /> : null}
          <Outlet />
          {location.pathname === "/" || location.pathname === "/explorer" ? <SeoFooter /> : null}
        </main>
        {user && user.email_verified !== false ? <BottomNav /> : null}
      </div>
      {reading ? (
        <Suspense fallback={null}>
          <PdfViewer edition={reading} onClose={closeReader} />
        </Suspense>
      ) : null}
      {user && location.pathname === "/ambassadeur" ? (
        <Suspense fallback={null}><ChatWidget /></Suspense>
      ) : null}
      <InstallPrompt />
      <NotificationPrompt />
      {user && user.paid_info_required ? <PaidInfoPrompt /> : user ? <ProfileLocationPrompt /> : null}
    </ReaderCtx.Provider>
  );
}
